import React from 'react';
import './ScheduledGameCard.css';

const ScheduledGameCard = ({
  game,
  index,
  playersById,
  sessionPlayerStatus,
  courtAssignments,
  waitingList,
  nextAssignments,
  onRemove,
  isInvalid,
  isDragging
}) => {
  const isOnCourt = game.status === 'placed' || game.status === 'on_court';
  const getPlayerLocation = (playerId) => {
    if (!playerId) return 'Not selected';
    
    const player = playersById[playerId];
    if (!player) return 'Player not found';
    
    // Check if on court
    const courtAssignment = courtAssignments.find(court => 
      court.team1.includes(playerId) || court.team2.includes(playerId)
    );
    
    if (courtAssignment) {
      const team = courtAssignment.team1.includes(playerId) ? 'Team 1' : 'Team 2';
      return `On Court ${courtAssignment.courtNumber} (${team})`;
    }
    
    // Check if in upcoming match
    const upcomingMatch = nextAssignments.find(match => 
      match.team1.includes(playerId) || match.team2.includes(playerId)
    );
    
    if (upcomingMatch) {
      const team = upcomingMatch.team1.includes(playerId) ? 'Team 1' : 'Team 2';
      return `Upcoming Match (${team})`;
    }
    
    // Check if in waiting list
    if (waitingList.includes(playerId)) {
      return 'Queue';
    }
    
    // Check status
    if (player.status === 'active') return 'Available';
    if (player.status === 'signed_up') return 'Signed Up';
    if (player.status === 'paused') return 'Paused';
    if (player.status === 'inactive') return 'Inactive';
    
    return 'Unknown';
  };

  const getPlayerInfo = (playerId) => {
    if (!playerId) return null;
    const player = playersById[playerId];
    if (!player) return null;
    
    // Get the actual status from sessionPlayerStatus
    const status = sessionPlayerStatus[playerId] || 'unknown';
    
    // Check if player is in upcoming match (valid for scheduled games)
    const isInUpcomingMatch = nextAssignments.some(match => 
      match.team1.includes(playerId) || match.team2.includes(playerId)
    );
    
    // Check if player is on court (invalid for promotion)
    const isOnCourt = courtAssignments.some(court => 
      court.team1.includes(playerId) || court.team2.includes(playerId)
    );
    
    // Check if player is in another upcoming match (invalid for promotion)
    const isInAnotherUpcoming = nextAssignments.some(match => 
      match.team1.includes(playerId) || match.team2.includes(playerId)
    );
    
    // Check if player status is invalid for promotion (must be 'active')
    const hasInvalidStatus = status !== 'active';
    
    const location = getPlayerLocation(playerId);
    const isInvalidForPromotion = isOnCourt || isInAnotherUpcoming || hasInvalidStatus;
    
    return {
      name: `${player.first_name} ${player.last_name}`,
      gamesPlayed: player.gamesPlayedThisSession || 0,
      location: location,
      status: status,
      isMissing: !player || (!['active', 'signed_up', 'paused'].includes(status) && !isInUpcomingMatch),
      isInvalidForPromotion: isInvalidForPromotion
    };
  };

  const team1Player1 = getPlayerInfo(game.team1_player1_id);
  const team1Player2 = getPlayerInfo(game.team1_player2_id);
  const team2Player1 = getPlayerInfo(game.team2_player1_id);
  const team2Player2 = getPlayerInfo(game.team2_player2_id);

  const players = [team1Player1, team1Player2, team2Player1, team2Player2];
  const hasMissingPlayers = players.some(p => p?.isMissing);
  const hasInvalidForPromotion = players.some(p => p?.isInvalidForPromotion);

  return (
    <div className={`scheduled-game-card ${isInvalid || hasMissingPlayers ? 'invalid' : ''} ${isDragging ? 'dragging' : ''} ${isOnCourt ? 'on-court' : ''}`}>
      <div className="flex justify-between items-start mb-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-text-secondary">#{index + 1}</span>
          {isOnCourt ? (
            <span className="text-xs bg-warning-light text-warning-dark px-2 py-1 rounded font-medium">
              In Progress
            </span>
          ) : (
            <span className="text-xs bg-surface-subtle text-text-secondary px-2 py-1 rounded">Game {index + 1}</span>
          )}
        </div>
        {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-sm font-bold leading-none text-danger-dark hover:bg-danger-light hover:text-danger focus:outline-none focus:ring-1 focus:ring-primary/30"
          title={isOnCourt ? 'Cancel in-progress scheduled game' : 'Remove scheduled game'}
          aria-label={isOnCourt ? 'Cancel in-progress scheduled game' : 'Remove scheduled game'}
        >
          ×
        </button>
        )}
      </div>

      <div className="space-y-2">
        {/* Team 1 */}
        <div className="bg-info-light p-2 rounded border-l-4 border-info">
          <div className="text-xs font-medium text-info-dark mb-1">Team 1</div>
          <div className="space-y-1">
            {team1Player1 && (
              <div className={`scheduled-player ${team1Player1.isMissing ? 'missing' : ''} ${team1Player1.isInvalidForPromotion ? 'invalid-for-promotion' : ''}`}>
                <div className="player-info">
                  <span className="font-medium">{team1Player1.name}</span>
                  <span className="player-games-badge">{team1Player1.gamesPlayed} games</span>
                </div>
                <div className={`player-location ${team1Player1.isInvalidForPromotion ? 'text-danger-dark font-medium' : 'text-text-muted'}`}>
                  {team1Player1.location}
                </div>
              </div>
            )}
            {team1Player2 && (
              <div className={`scheduled-player ${team1Player2.isMissing ? 'missing' : ''} ${team1Player2.isInvalidForPromotion ? 'invalid-for-promotion' : ''}`}>
                <div className="player-info">
                  <span className="font-medium">{team1Player2.name}</span>
                  <span className="player-games-badge">{team1Player2.gamesPlayed} games</span>
                </div>
                <div className={`player-location ${team1Player2.isInvalidForPromotion ? 'text-danger-dark font-medium' : 'text-text-muted'}`}>
                  {team1Player2.location}
                </div>
              </div>
            )}
          </div>
        </div>

        <div
          className="flex justify-center py-1"
          role="separator"
          aria-label="versus"
        >
          <span className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">
            vs
          </span>
        </div>

        {/* Team 2 */}
        <div className="bg-surface-subtle p-2 rounded border-l-4 border-primary">
          <div className="text-xs font-medium text-primary mb-1">Team 2</div>
          <div className="space-y-1">
            {team2Player1 && (
              <div className={`scheduled-player ${team2Player1.isMissing ? 'missing' : ''} ${team2Player1.isInvalidForPromotion ? 'invalid-for-promotion' : ''}`}>
                <div className="player-info">
                  <span className="font-medium">{team2Player1.name}</span>
                  <span className="player-games-badge">{team2Player1.gamesPlayed} games</span>
                </div>
                <div className={`player-location ${team2Player1.isInvalidForPromotion ? 'text-danger-dark font-medium' : 'text-text-muted'}`}>
                  {team2Player1.location}
                </div>
              </div>
            )}
            {team2Player2 && (
              <div className={`scheduled-player ${team2Player2.isMissing ? 'missing' : ''} ${team2Player2.isInvalidForPromotion ? 'invalid-for-promotion' : ''}`}>
                <div className="player-info">
                  <span className="font-medium">{team2Player2.name}</span>
                  <span className="player-games-badge">{team2Player2.gamesPlayed} games</span>
                </div>
                <div className={`player-location ${team2Player2.isInvalidForPromotion ? 'text-danger-dark font-medium' : 'text-text-muted'}`}>
                  {team2Player2.location}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {(isInvalid || hasMissingPlayers) && (
        <div className="mt-2 p-2 bg-danger-light border border-danger rounded text-xs text-danger-dark">
          ⚠️ This game has missing or invalid players and cannot be filled
        </div>
      )}
      
      {hasInvalidForPromotion && !isInvalid && !hasMissingPlayers && (
        <div className="mt-2 p-2 bg-warning-light border border-warning rounded text-xs text-warning-dark">
          ⚠️ Can't add to upcoming court
        </div>
      )}
    </div>
  );
};

export default ScheduledGameCard;
